/**
 * Lupa de la foto de la ficha: un recuadro sobre la foto marca la zona, y un
 * panel al costado la muestra ampliada (el patron de las fichas de Falabella).
 *
 * Solo corre con mouse y en escritorio: en un telefono no hay puntero que
 * pase por encima ni costado donde poner el panel, y un toque tiene que
 * seguir siendo un toque. Las cuentas se hacen sobre el rectangulo que la
 * foto OCUPA de verdad dentro de su caja —con `object-fit:contain` una foto
 * alta deja franjas a los lados—, para que la lupa no recorra el blanco.
 */
import { useCallback, useState } from 'react';
import type { PointerEvent as EventoPuntero } from 'react';

/** Cuanto se amplia en el panel respecto de la foto tal como se ve. */
const AUMENTO = 2.5;

/** Donde la lupa tiene sentido: con mouse y con la ficha en dos columnas. */
const CONSULTA_ESCRITORIO = '(hover: hover) and (pointer: fine) and (min-width: 1081px)';

interface Rectangulo {
  x: number;
  y: number;
  ancho: number;
  alto: number;
}

export interface EstadoZoom {
  /** El recuadro sobre la foto, relativo a la caja de la foto. */
  lente: Rectangulo;
  /** La foto ampliada dentro del panel: su tamano y su desplazamiento. */
  fondo: Rectangulo;
}

/** El rectangulo que ocupa la imagen dentro de su caja con `object-fit:contain`. */
function areaVisible(imagen: HTMLImageElement, caja: DOMRect): Rectangulo {
  const rect = imagen.getBoundingClientRect();
  const proporcion = imagen.naturalWidth / imagen.naturalHeight || 1;
  const ancho = Math.min(rect.width, rect.height * proporcion);
  const alto = ancho / proporcion;
  return {
    x: rect.left - caja.left + (rect.width - ancho) / 2,
    y: rect.top - caja.top + (rect.height - alto) / 2,
    ancho,
    alto,
  };
}

/** Ubica un tramo de largo `largo` centrado en `centro`, sin salirse de [inicio, inicio + total]. */
function acotar(centro: number, largo: number, inicio: number, total: number): number {
  if (largo >= total) return inicio + (total - largo) / 2;
  return Math.min(Math.max(centro - largo / 2, inicio), inicio + total - largo);
}

export function useZoomFoto() {
  const [zoom, setZoom] = useState<EstadoZoom | null>(null);

  const mover = useCallback((evento: EventoPuntero<HTMLElement>) => {
    if (evento.pointerType !== 'mouse' || !window.matchMedia(CONSULTA_ESCRITORIO).matches) return;
    const caja = evento.currentTarget;
    const imagen = caja.querySelector('img');
    if (imagen === null || !imagen.complete || imagen.naturalWidth === 0) return;

    const rectCaja = caja.getBoundingClientRect();
    const area = areaVisible(imagen, rectCaja);
    // El panel mide lo mismo que la caja de la foto, asi que la lente es la
    // caja dividida por el aumento.
    const lenteAncho = Math.min(rectCaja.width / AUMENTO, area.ancho);
    const lenteAlto = Math.min(rectCaja.height / AUMENTO, area.alto);
    const lenteX = acotar(evento.clientX - rectCaja.left, lenteAncho, area.x, area.ancho);
    const lenteY = acotar(evento.clientY - rectCaja.top, lenteAlto, area.y, area.alto);

    setZoom({
      lente: { x: lenteX, y: lenteY, ancho: lenteAncho, alto: lenteAlto },
      fondo: {
        x: -(lenteX - area.x) * AUMENTO,
        y: -(lenteY - area.y) * AUMENTO,
        ancho: area.ancho * AUMENTO,
        alto: area.alto * AUMENTO,
      },
    });
  }, []);

  const salir = useCallback(() => {
    setZoom(null);
  }, []);

  return { zoom, mover, salir };
}
